import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
  actionState,
  currentThreadIdState,
  elementState,
  firstUserInteraction,
  getOrCreateSessionId,
  messagesState,
  resumeThreadErrorState,
  sessionIdState,
  sideViewState,
  tasklistState,
  tokenCountState,
  useChatInteract,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

interface Props {
  id: string;
}

export default function AutoResumeThread({ id }: Props) {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { setIdToResume } = useChatInteract();
  const { session, idToResume } = useChatSession();
  const [resumeThreadError, setResumeThreadError] = useRecoilState(
    resumeThreadErrorState
  );
  const setSessionId = useSetRecoilState(sessionIdState);
  const setMessages = useSetRecoilState(messagesState);
  const setElements = useSetRecoilState(elementState);
  const setTasklists = useSetRecoilState(tasklistState);
  const setActions = useSetRecoilState(actionState);
  const setTokenCount = useSetRecoilState(tokenCountState);
  const setSideView = useSetRecoilState(sideViewState);
  const setCurrentThreadId = useSetRecoilState(currentThreadIdState);
  const setFirstUserInteraction = useSetRecoilState(firstUserInteraction);

  useEffect(() => {
    if (!config?.threadResumable) return;
    // Multi-session-per-tab: each thread has its own backend
    // WebsocketSession. Navigating between threads disconnects from the
    // current thread's session (without emitting clear_session, so the
    // backend keeps it alive for the chainlit session_timeout window) and
    // connects to the target thread's session. If the target was visited
    // before, we restore that session and any in-flight on_message task's
    // emitter rebinds to this new socket, resuming live streaming. If
    // not, a fresh session is created. Reload on the same thread is a
    // no-op session-wise (same id) so it goes straight to restore.
    const targetSessionId = getOrCreateSessionId(id);
    if (session?.socket) {
      session.socket.disconnect();
      // Reset visible UI state so we don't briefly render the old
      // thread's messages while resume_thread arrives for the target.
      setMessages([]);
      setElements([]);
      setTasklists([]);
      setActions([]);
      setTokenCount(0);
      setSideView(undefined);
      setCurrentThreadId(undefined);
      setFirstUserInteraction(undefined);
    }
    setSessionId(targetSessionId);
    setIdToResume(id);
    if (!config?.dataPersistence) {
      navigate('/');
    }
  }, [config?.threadResumable, id]);

  useEffect(() => {
    if (id !== idToResume) {
      return;
    }
    if (session?.error) {
      toast.error("Couldn't resume chat");
      navigate('/');
    }
  }, [session, idToResume, id]);

  useEffect(() => {
    if (resumeThreadError) {
      toast.error("Couldn't resume chat: " + resumeThreadError);
      navigate('/');
      setResumeThreadError(undefined);
    }
  }, [resumeThreadError]);

  return null;
}
